/**
 * PlanProposalCard.logic — pure helpers for the master-initiated plan
 * proposal UI (M4.5).
 *
 * Exported pure functions are unit-tested independently from the React layer.
 */

import type { CmmdAnchorKind } from "@cmmd-forge/contracts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Subset of AutoCreateTaskPromptItem needed by the UI. */
export interface PlanProposalItem {
  readonly slug: string;
  readonly title: string;
  readonly summary: string;
}

// ---------------------------------------------------------------------------
// buildPlanProposalSummary
// ---------------------------------------------------------------------------

/**
 * Summarise a plan as a header string, e.g. "3 workers, 3 tasks".
 * The worker count and task count are always equal (one task per worker).
 */
export function buildPlanProposalSummary(items: readonly PlanProposalItem[]): string {
  const n = items.length;
  if (n === 0) return "No workers planned";
  const workers = n === 1 ? "1 worker" : `${n} workers`;
  const tasks = n === 1 ? "1 task" : `${n} tasks`;
  return `${workers}, ${tasks}`;
}

// ---------------------------------------------------------------------------
// buildAnchorLabel
// ---------------------------------------------------------------------------

const ANCHOR_KIND_LABELS: Record<CmmdAnchorKind, string> = {
  campaign: "Campaign",
  project: "Project",
  task: "Task",
  scratch: "workspace",
};

/**
 * Human-readable label for where tasks will be created, e.g. "Project" or
 * "Campaign" (with ID suffix when available).
 */
export function buildAnchorLabel(anchorKind: CmmdAnchorKind, anchorCmmdId: string | null): string {
  const base = ANCHOR_KIND_LABELS[anchorKind];
  if (!anchorCmmdId) return base;
  // Truncate long IDs for display
  const shortId = anchorCmmdId.length > 12 ? `…${anchorCmmdId.slice(-8)}` : anchorCmmdId;
  return `${base} (${shortId})`;
}
